import React, { useEffect, useMemo, useState, useRef, useCallback, memo } from 'react'
import cn from 'classnames'

import Button from '../Button/Button'
import Icon from '../Icon2'
import FieldLabel from '../FieldLabel/FieldLabel'
import HelpText from '../HelpText/HelpText'
import Field from '../Field'
import Tooltip from '../Tooltip/Tooltip'
import RuleBlock from './RuleBlock'

import { RuleBuilderProps, InternalRule, SelectedModuleValueObj, Rule } from './types'
import { updateRules } from './helper'

import { isEqual, cloneDeep, omit } from 'lodash'

import './RuleBuilder.scss'

const RuleBuilder: React.FC<RuleBuilderProps> = (props) => {
    let {
        vendor = 'cms',
        rules,
        onRulesChange,
        ruleBuildFor,
        title,
        subTitle,
        isCANRule,
        testId,
        onlyBuilder = false,
        className,
        permissionSubActionMapping,
        ruleDescription,
        ruleBlockHelpText,
        permissionActionOptions,
        permissionToModuleOptions,
        hasBranchFeature = false,
        openItemsChooseModal,
        fetchItemsData,
        enableLottiePreview = false,
        assetPickerModalMultiBranchInfo,
        t = (key) => '',
    } = props

    // Reorder permissionSubActionMapping to ensure parent action key appears first in its array
    // Eg: create: ['read', 'create', 'update'] -> ['create', 'read', 'update']
    const normalizedPermissionMapping = useMemo(() => {
        const normalized = {}

        Object.entries(permissionSubActionMapping).forEach(([key, actions]) => {
            if (!Array.isArray(actions)) return

            const actionsCopy = [...actions]
            const keyIndex = actionsCopy.indexOf(key)

            if (keyIndex > 0) {
                // Remove key from its current position and add it to the beginning
                actionsCopy.splice(keyIndex, 1)
                actionsCopy.unshift(key)
            } else if (keyIndex === -1) {
                // If key is not in the array, add it to the beginning
                actionsCopy.unshift(key)
            }

            normalized[key] = actionsCopy
        })

        return normalized
    }, [permissionSubActionMapping])

    const ruleFor = typeof title === 'string' ? title.toLowerCase() : ruleBuildFor.toLowerCase()
    const dataTestId = testId || `cs-role-${ruleFor}-${isCANRule ? 'can' : 'cannot'}-rules`
    const containerClasses = cn('RuleBuilder', className)

    const permissionKeys = useMemo(() => Object.keys(permissionActionOptions), [permissionActionOptions])

    const getPermissionActions = (action: string) => normalizedPermissionMapping[action] || []

    const getPermissionSelectOptionsFn = useMemo(() => {
        return (permissions: string[], permissionOptions: any) => {
            return permissions.map((permission) => ({
                value: permission,
                label: permissionOptions[permission] || permission,
                testId: `cs-role-permission-${permission}`
            }))
        }
    }, [permissionKeys])

    const permissionSelectOptions = getPermissionSelectOptionsFn(permissionKeys, permissionActionOptions)

    const permissionOptionsMap = useMemo(() => {
        const map = new Map()
        permissionSelectOptions.forEach((action) => map.set(action.value, action))
        return map
    }, [permissionSelectOptions])

    const moduleOptionsMap = useMemo(() => {
        const map = new Map()
        permissionToModuleOptions.forEach((module) => map.set(module.value, module))
        return map
    }, [permissionToModuleOptions])

    const [internalRules, setInternalRules] = useState<InternalRule[]>([])
    const [selectedModuleValues, setSelectedModuleValues] = useState<SelectedModuleValueObj>({})
    const prevRulesRef = useRef<any[]>([])
    const isUpdatingFromExternalRef = useRef(false)
    const isUpdatingFromInternalRef = useRef(false)
    const ruleKeysRef = useRef<{ [key: number]: string }>({})

    // Function to sync external rules into internal format
    const syncRulesToInternal = useCallback((inputRules: Rule[]) => {
        const newSelectedModuleValues: SelectedModuleValueObj = {}

        const updatedRules = inputRules.map((rule, i) => {
            const actionObj = rule.action || {}
            const { action, ...rest } = rule
            let module = moduleOptionsMap.get(rule.module) || {}
            let moduleKey = module.value === 'folder' ? 'folders' : module.value === 'asset' ? 'assets' : '$all'
            if (ruleBuildFor === 'locale') {
                module = 'locale'
                moduleKey = module + 's'
            }

            // Collect selected module values
            newSelectedModuleValues[i] = {
                [moduleKey]: rest[moduleKey]
            }

            let shouldShowAllPermissions = actionObj?.['$all'] === true
            if (!shouldShowAllPermissions) {
                const individualPermissions = Object.keys(actionObj)
                shouldShowAllPermissions =
                    individualPermissions.length > 0 && permissionKeys.every((key) => key == '$all' ? true : actionObj[key] === true)
            }

            let actions = []
            actions = Object.keys(actionObj)
                .filter((key) => key !== '$all' && actionObj[key] === true)
                .map((key) => permissionOptionsMap?.get(key))
                .filter(Boolean)
            if (shouldShowAllPermissions) {
                actions.unshift(permissionOptionsMap?.get('$all'))
            }

            // Generate or reuse existing ruleKey to maintain consistency
            if (!ruleKeysRef.current[i]) {
                ruleKeysRef.current[i] = Math.random().toString(36).substring(2, 15)
            }

            return {
                ...rest,
                ruleKey: ruleKeysRef.current[i],
                actions,
                module: module,
                ...(moduleKey === '$all' ? {} : { [moduleKey]: rest?.[moduleKey] || [] })
            } as InternalRule
        })

        return { updatedRules, newSelectedModuleValues }
    }, [moduleOptionsMap, permissionOptionsMap, ruleBuildFor, permissionKeys])

    // Initialize and handle external rule changes
    useEffect(() => {
        // Check if rules have actually changed to prevent unnecessary updates
        if (isEqual(prevRulesRef.current, rules)) return;

        isUpdatingFromExternalRef.current = true
        const { updatedRules, newSelectedModuleValues } = syncRulesToInternal(rules)

        setInternalRules(updatedRules)
        setSelectedModuleValues(newSelectedModuleValues)
        prevRulesRef.current = cloneDeep(rules) // Deep copy to prevent reference issues

        // Reset the flag after state updates
        setTimeout(() => {
            isUpdatingFromExternalRef.current = false
        }, 0)
    }, [rules, syncRulesToInternal])

    // Handle side effects related to selectedModuleValues
    const mergeSelectedValues = useCallback((rule: any, selectedValues: any) => {
        if (rule?.module?.value === 'folder' || rule?.module === 'folder') {
            return { ...rule, folders: selectedValues?.folders || [] }
        } else if (rule?.module?.value === 'asset' || rule?.module === 'asset') {
            return { ...rule, assets: selectedValues?.assets || [] }
        } else if (rule?.module?.value === 'locale' || rule?.module === 'locale') {
            const localesValues = selectedValues?.locales || []
            return {
                ...rule,
                locales: localesValues.map((loc) => (typeof loc === 'object' ? omit(loc, 'label') : loc))
            }
        }

        return rule
    }, [])

    useEffect(() => {
        // Skip if this update is coming from external rules change or internal actions
        if (isUpdatingFromExternalRef.current || isUpdatingFromInternalRef.current || Object.keys(selectedModuleValues).length === 0) {
            return
        }

        // Update both internal and external rules
        const updatedInternalRules = []
        const updatedExternalRules = []

        for (let index = 0; index < internalRules.length; index++) {
            const internalRule = internalRules[index]
            const externalRule = rules[index]
            const selectedValues = selectedModuleValues[index]

            updatedInternalRules.push(mergeSelectedValues(internalRule, selectedValues))
            updatedExternalRules.push(mergeSelectedValues(externalRule, selectedValues))
        }

        setInternalRules(updatedInternalRules)
        onRulesChange(updatedExternalRules)
    }, [selectedModuleValues])

    const handlePermissionActionChange = useCallback((idxToUpdate: number, value?: any) => {
        // console.log('debug_handlePermissionActionChange', { idxToUpdate, value })
        isUpdatingFromInternalRef.current = true
        updateRules({
            idxToUpdate,
            updateType: 'action',
            newValues: value,
            internalRules,
            permissionOptionsMap,
            setSelectedModuleValues,
            onRulesChange,
            setInternalRules,
            actionProcessor: (val) => (val[0] ? getPermissionActions(val[0].value) : [])
        })
        setTimeout(() => {
            isUpdatingFromInternalRef.current = false
        }, 0)
    }, [internalRules, permissionOptionsMap, onRulesChange, getPermissionActions])

    const handleModuleChange = useCallback((idxToUpdate: number, value?: any) => {
        // console.log('debug_handlePermissionToChange', { idxToUpdate, value })
        isUpdatingFromInternalRef.current = true
        updateRules({
            idxToUpdate,
            updateType: 'module',
            newValues: value,
            internalRules,
            permissionOptionsMap,
            setSelectedModuleValues,
            onRulesChange,
            setInternalRules
        })
        setTimeout(() => {
            isUpdatingFromInternalRef.current = false
        }, 0)
    }, [internalRules, permissionOptionsMap, onRulesChange])

    console.log('debug_RuleBuilder_rule_state', { rules, internalRules })

    const handleDeleteRuleBlock = useCallback((idxToDelete: number) => {
        const updatedInternalRules = internalRules.filter((_, i) => i !== idxToDelete)
        const updatedExternalRules = rules.filter((_, i) => i !== idxToDelete)

        // Clean up ruleKeys for deleted items and reindex
        const newRuleKeys = {}
        Object.keys(ruleKeysRef.current).forEach((key) => {
            const numKey = parseInt(key)
            if (numKey < idxToDelete) {
                newRuleKeys[numKey] = ruleKeysRef.current[numKey]
            } else if (numKey > idxToDelete) {
                newRuleKeys[numKey - 1] = ruleKeysRef.current[numKey]
            }
        })
        ruleKeysRef.current = newRuleKeys

        setSelectedModuleValues((prev) => {
            const newValues = { ...prev }
            delete newValues[idxToDelete]
            // Reindex remaining values to maintain proper order
            const reindexedValues = {}
            Object.keys(newValues).forEach((key, index) => {
                reindexedValues[index] = newValues[key]
            })
            return reindexedValues
        })
        onRulesChange(updatedExternalRules)
        setInternalRules(updatedInternalRules)
    }, [internalRules, rules, onRulesChange])

    const handleAddRuleBlock = useCallback(() => {
        const isLocale = ruleBuildFor === 'locale'
        const newIndex = internalRules.length

        // Generate new ruleKey for the new rule
        ruleKeysRef.current[newIndex] = Math.random().toString(36).substring(2, 15)

        const newInternalRule: InternalRule = {
            isCan: isCANRule,
            actions: [],
            module: isLocale ? 'locale' : permissionToModuleOptions[0],
            ruleKey: ruleKeysRef.current[newIndex]
        }
        onRulesChange([...rules, { action: {}, module: isLocale ? 'locale' : '$all', isCan: isCANRule }])
        setInternalRules([...internalRules, newInternalRule])
    }, [ruleBuildFor, isCANRule, permissionToModuleOptions, internalRules, rules, onRulesChange])

    // Memoize the rendered rule blocks to prevent unnecessary re-renders
    const renderedRuleBlocks = useMemo(() => {
        return internalRules?.map((rule: InternalRule, i: any) => {
            return (
                <RuleBlock
                    key={`${i}-${rule?.ruleKey}`}
                    index={i}
                    rule={rule}
                    vendor={vendor}
                    fetchItemsData={fetchItemsData}
                    ruleBuildFor={ruleBuildFor}
                    hasBranchFeature={hasBranchFeature}
                    uniqueKey={`${dataTestId}-rule-block-${i}`}
                    selectedModuleValue={selectedModuleValues[i]}
                    setSelectedModuleValues={setSelectedModuleValues}
                    onModuleChange={handleModuleChange}
                    onDeleteRuleBlock={handleDeleteRuleBlock}
                    onPermissionActionChange={handlePermissionActionChange}
                    selectActionOptions={permissionSelectOptions}
                    selectModuleOptions={permissionToModuleOptions}
                    openItemsChooseModal={openItemsChooseModal}
                    enableLottiePreview={enableLottiePreview}
                    assetPickerModalMultiBranchInfo={assetPickerModalMultiBranchInfo}
                />
            )
        })
    }, [
        internalRules,
        selectedModuleValues,
        vendor,
        fetchItemsData,
        ruleBuildFor,
        hasBranchFeature,
        dataTestId,
        handleModuleChange,
        handleDeleteRuleBlock,
        handlePermissionActionChange,
        permissionSelectOptions,
        permissionToModuleOptions,
        openItemsChooseModal,
        enableLottiePreview,
        assetPickerModalMultiBranchInfo
    ])

    return (
        <div className={containerClasses} data-test-id={dataTestId + '-container'}>
            <Field>
                {/* RuleBuilder Title */}
                {!onlyBuilder && title ? (
                    <>
                        <FieldLabel htmlFor={ruleFor} version="v2" testId={`${dataTestId}-title`}>
                            {title}
                        </FieldLabel>
                        {ruleDescription ? <HelpText>{ruleDescription}</HelpText> : null}
                    </>
                ) : null}
                {/* RuleBuilder Section - Main body */}
                <div className="RuleBuilder__section">
                    <div className={`${isCANRule ? 'RuleBuilder__section--can' : 'RuleBuilder__section--cannot'}`}>
                        {!onlyBuilder ? (
                            <FieldLabel
                                htmlFor={isCANRule ? 'thisRoleCan' : 'thisRoleCannot'}
                                version="v2"
                                testId={`${dataTestId}-${isCANRule ? 'can' : 'cannot'}-label`}>
                                {isCANRule ? (t('RuleBuilder.this_role_can') || 'This role CAN:') : (t('RuleBuilder.this_role_cannot') || 'This role CANNOT:')}
                            </FieldLabel>
                        ) : null}
                        <div className="RuleBuilder__content">
                            <div className="RuleBuilder__content--section">
                                {/* RuleBuilder Section Heading */}
                                {!onlyBuilder ? (
                                    <div className="RuleBuilder__content--section--heading">
                                        <p data-test-id={`${dataTestId}-sub-title`}>{subTitle}</p>
                                        <Tooltip content={ruleBlockHelpText} position="right" interactive={false}>
                                            <div style={{ cursor: 'pointer' }}>
                                                <Icon
                                                    className="Docs__icon"
                                                    icon="Help"
                                                    version="v2"
                                                    size="small"
                                                    testId={`${dataTestId}-help-icon`}
                                                />
                                            </div>
                                        </Tooltip>
                                    </div>
                                ) : null}
                                {/* Rule block Separator */}
                                <div className="separator-vertical-wrapper">
                                    <div className="separator-vertical" />
                                    {renderedRuleBlocks}
                                </div>
                                {/* Add Rule Button */}
                                <Button
                                    buttonType="link"
                                    testId={`${dataTestId}-add-condition`}
                                    version="v2"
                                    onClick={handleAddRuleBlock}
                                    icon="v2-Plus"
                                    className="mt-12">
                                    Add Rule
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </Field>
        </div>
    )
}

export default memo(RuleBuilder)

RuleBuilder.displayName = 'RuleBuilder'
